import React, { useState, useEffect } from "react";
import styles from "./PrintHistory.module.css";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Button, Select } from "@sainsburys-tech/fable";
import { useGetPostsQuery } from "../../../api/apiConfig";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface Location {
  LocationId: string;
  Name: string;
  Status: string;
}

interface IState {
  IsRetired: boolean;
  Status: string;
  Configuration: {
    IP: string;
    IsPublished: boolean;
    IsShared: boolean;
    Port: number;
    Type: string;
    LabelMediaType: string;
  };
  Identity: {
    Name: string;
    SBPrinterId: string;
    SerialNumber: string;
    Location: {
      Id: string;
      Name: string;
      WarehouseFloor: string;
      WarehouseLocality: string;
    };
    PrinterModel: {
      Id: string;
      Name: string;
    };
  };
  StatusOverride: {
    IsAllowed: boolean;
    PreventTill: null;
    StatusCode: string;
    StatusDescription: string;
    LastUpdate: Date;
  };
}

interface PrintingRequest {
  Id: string;
  Printer: {
    Id: string;
    SBPrinterId: string;
    IP: string;
    Port: number;
    Type: string;
  };
  Template: {
    Id: string;
    SBTemplateId: string;
    ZPLTemplate: string;
  };
  Printing: {
    Copies: number;
    TemplateParameterData: string;
    Status: string;
    StatusDesc: string;
  };
  Audit: {
    CreatedBy: string | null;
    CreatedOn: string;
    UpdatedBy: string | null;
    UpdatedOn: string;
  };
}

interface Job {
  submitted: string;
  user: string | null;
  printerId: string;
  jobStatus: string;
  jobName: string;
}

const PrintHistory: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [submittedFilter, setSubmittedFilter] = useState("");
  const [location, setLocation] = useState<Location[]>([]);
  const [printerNames, setPrinterNames] = useState<string[]>([]);
  const [selectedLocation, setSelectedLocation] = useState({
    id: "",
    name: ""
  });
  const [selectedPrinter, setSelectedPrinter] = useState("");
  const [queryParams, setQueryParams] = useState({ fromDate: "", toDate: "" });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showData, setShowData] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const {
    isLoading: isLocationLoading,
    isSuccess: isLocationSuccess,
    data: getAllLocations
  } = useGetPostsQuery("locations/GetLocationFromIdentityToken");

  const {
    isLoading,
    isSuccess,
    data: printerList
  } = useGetPostsQuery(
    `printers/GetByLocationId?LocationId=${
      selectedLocation?.id ? selectedLocation?.id : ""
    }`
  );

  const { data: printHistoryData, refetch: fetchUsageLogs } = useGetPostsQuery(
    `PrintingRequest/GetPrintHistory?locationId=${
      selectedLocation?.id ? selectedLocation?.id : ""
    }&SBPrinterId=${selectedPrinter !== "" ? selectedPrinter : ""}&fromDate=${
      queryParams.fromDate
    }&toDate=${queryParams?.toDate}&PageNumber=${page}&PageSize=${rowsPerPage}`
  );

  const apiData = printHistoryData?.Model as { [key: string]: any };

  useEffect(() => {
    if (isLocationSuccess && getAllLocations?.Model) {
      const validLocations = getAllLocations.Model.filter(
        (location: any): location is Location =>
          location.LocationId &&
          location.Name &&
          location.Status
      );

      const sortedLocations: Location[] = validLocations.sort(
        (a: any, b: any) => a.Name.localeCompare(b.Name)
      );
      setLocation(sortedLocations);
    }
  }, [isLocationSuccess]);

  useEffect(() => {
    if (isSuccess && printerList?.Model) {
      const names = (printerList.Model as IState[])
        .map((printer) => printer?.Identity?.Name)
        .sort((a, b) => a.localeCompare(b));

      setPrinterNames(names);
    }
  }, [isSuccess, printerList]);
  const generateLabels = (startDate: any, days: any) => {
    const labels = [];
    for (let i = 0; i < days; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i - 1);
      labels.push(
        currentDate.toLocaleDateString("en-US", { weekday: "short" })
      );
    }
    return labels;
  };

  const fromDateChart = new Date(queryParams.fromDate);
  let toDateChart = new Date(queryParams.toDate);
  toDateChart.setDate(toDateChart.getDate() - 1);

  const days =
    Math.ceil(
      (toDateChart.getTime() - fromDateChart.getTime()) / (1000 * 60 * 60 * 24)
    ) + 1;

  const dateToPrintCount: { [key: string]: number | null } = {};
  apiData?.DailyPrintCounts?.forEach((data: any) => {
    const dateStr = `${data.Year}-${String(data.Month).padStart(
      2,
      "0"
    )}-${String(data.Day).padStart(2, "0")}`;
    dateToPrintCount[dateStr] = data.PrintCount;
  });

  const printCounts: (number | null)[] = [];
  for (let i = 0; i < days; i++) {
    const currentDate = new Date(fromDateChart);
    currentDate.setDate(fromDateChart.getDate() + i);
    const dateStr = currentDate.toISOString().split("T")[0];
    printCounts.push(dateToPrintCount[dateStr] || 0);
  }

  const labels = generateLabels(fromDateChart, days);

  const data = {
    labels: generateLabels(fromDateChart, days),
    datasets: [
      {
        label: "Printed Items",
        data: printCounts,
        backgroundColor: "#f06c00"
      }
    ]
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: false
      }
    },
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 10,
        bottom: 0
      }
    },
    scales: {
      x: {
        ticks: {
          callback: function (value: any): string {
            return labels[value];
          },
          maxRotation: 40,
          minRotation: 30,
          autoSkip: false // Ensure all labels are shown
        },
        font: {
          size: 6
        }
      }
    }
  };

  const handleNextPage = () => {
    if (
      apiData &&
      apiData?.datawithRecordCount &&
      page <
        Math.ceil(apiData?.datawithRecordCount.RecordCount || 0 / rowsPerPage)
    ) {
      setPage(page + 1);
    }
  };

  const handlePreviousPage = () => {
    if (apiData && page > 1) {
      setPage(page - 1);
    }
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const transformData = (printingRequests: PrintingRequest[]): Job[] => {
    return printingRequests?.map((request) => ({
      submitted: new Date(request?.Audit?.CreatedOn).toLocaleString("en-UK", {
        timeZone: "Europe/London",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true
      }),
      user: request?.Audit?.CreatedBy || "-",
      printerId: request?.Printer?.SBPrinterId,
      jobStatus:
        request?.Printing?.Status === "S"
          ? "Completed"
          : request?.Printing?.Status === "U"
          ? "Failed"
          : request?.Printing?.Status === "A"
          ? "Pending"
          : "",
      jobName: request?.Template?.SBTemplateId
    }));
  };

  const parseDate = (dateString: any) => {
    const [date, time, period] = dateString.split(/, | /);
    const [day, month, year] = date.split("/");
    const [hours, minutes, seconds] = time.split(":");
    let hours24 = parseInt(hours, 10);
    if (period === "PM" && hours24 !== 12) {
      hours24 += 12;
    } else if (period === "AM" && hours24 === 12) {
      hours24 = 0;
    }
    return new Date(year, month - 1, day, hours24, minutes, seconds);
  };

  const filterJobs = (jobs: any) => {
    let filtered = jobs;

    if (statusFilter) {
      filtered = filtered.filter((job: any) => job.jobStatus === statusFilter);
    }

    if (submittedFilter) {
      const now = new Date();
      filtered = filtered.filter((job: any) => {
        const jobDate = parseDate(job.submitted);
        if (submittedFilter === "Today") {
          return jobDate.toDateString() === now.toDateString();
        } else if (submittedFilter === "Yesterday") {
          const yesterday = new Date(now);
          yesterday.setDate(now.getDate() - 1);
          return jobDate.toDateString() === yesterday.toDateString();
        } else if (submittedFilter === "Last 7 Days") {
          const lastWeek = new Date(now);
          lastWeek.setDate(now.getDate() - 7);
          return jobDate >= lastWeek && jobDate <= now;
        }
        return true;
      });
    }

    if (searchTerm) {
      filtered = filtered.filter((job: any) =>
        job.jobName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };
  const jobs = transformData(apiData?.datawithRecordCount.LstRecords);

  const filteredJobs = filterJobs(jobs);

  const handleLocationChange = (value: string) => {
    const selectedOption = location.find((loc: any) => loc.Name === value);
    setSelectedLocation({
      id: selectedOption?.LocationId ?? "",
      name: selectedOption?.Name ?? ""
    });
    setSelectedPrinter("");
  };

  const handlePrinterChange = (value: string) => {
    setSelectedPrinter(value);
  };

  const getLastWeekDataforAPI = () => {
    const currentDate = new Date();
    const fromDate = new Date();
    fromDate.setUTCHours(0, 0, 0, 0);

    fromDate.setDate(currentDate.getDate() - 6);

    return {
      fromDate: fromDate.toISOString(),
      toDate: currentDate.toISOString()
    };
  };
  const getLastWeekData = () => {
    let currentDate = new Date();
    currentDate.setDate(currentDate.getDate() - 1);

    const fromDate = new Date();
    fromDate.setDate(currentDate.getDate() - 6);

    return {
      fromDate: fromDate.toLocaleDateString(),
      toDate: currentDate.toLocaleDateString()
    };
  };

  const { fromDate, toDate } = getLastWeekData();

  useEffect(() => {
    const { fromDate, toDate } = getLastWeekDataforAPI();
    setQueryParams({ fromDate, toDate });
  }, []);

  const fetchData = () => {
    setIsSubmitted(true);
    if (selectedLocation.id) {
      setShowData(true);
      fetchUsageLogs();
    } else {
      console.log("Please select location...");
    }
  };

  function convertTo12HourFormat(hour: any) {
    const period = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12} ${period}`;
  }

  return (
    <div className={styles.container}>
      <h4>Print History</h4>
      <p>
        Filter and view your Print Jobs to review your SmartPrint print
        activity.
      </p>
      <div className={styles.printSummary}>
        <div className={`${styles.dropdownContainer} ${styles.orange}`}>
          <Select
            label="Location"
            options={[
              { label: "Select Location", value: "default" },
              ...(isLocationLoading
                ? [
                    {
                      label: "Loading...",
                      value: "loading",
                      isDisabled: isLocationLoading
                    }
                  ]
                : location?.map((loc: any) => ({
                    value: loc?.Name,
                    label: loc?.Name,
                    id: loc?.Id
                  })))
            ]}
            style={{ textAlign: "left" }}
            name="Identity.Location.Name"
            onChange={(value: any) => handleLocationChange(value)}
            value={selectedLocation.name}
          />

          <Select
            label="Printer"
            options={[
              { label: "Select Printer", value: "default" },
              ...(isLoading
                ? [
                    {
                      label: "Loading...",
                      value: "loading",
                      isDisabled: isLoading
                    }
                  ]
                : printerNames &&
                  printerNames?.map((Model: any) => ({
                    label: Model,
                    value: Model
                  })))
            ]}
            value={selectedPrinter}
            onChange={(value: any) => handlePrinterChange(value)}
          />
          <br />
        </div>
      </div>
      {isSubmitted && selectedLocation?.id === "" ? (
        <div className={styles.error}>{"Please select location"}</div>
      ) : (
        ""
      )}

      <Button className={styles.goButton} onClick={() => fetchData()}>
        View
      </Button>
      <br />
      {showData !== true ? (
        <div className={styles.statusMessage}>Please choose a location</div>
      ) : (
        <>
          <span className={styles.summaryText}>
            Last 7 days Print Job Summary
          </span>

          <div className={styles.summaryRow}>
            <div className={styles.summaryColumn}>
              <h5>PRINTED ITEMS</h5>
              <p className={styles.orangeText}>
                {apiData?.Printcount !== 0 ? apiData?.Printcount : 0}
              </p>
              <footer>{`${fromDate} - ${toDate}`}</footer>
            </div>
            <div className={styles.summaryColumn}>
              <h5>AVERAGE PRINTS PER DAY</h5>
              <p>
                {apiData?.DailyAvg !== 0 ? Math.ceil(apiData?.DailyAvg) : 0}
              </p>
              <footer>{`${fromDate} - ${toDate}`}</footer>
            </div>
            <div className={styles.summaryColumn}>
              <h5>HIGHEST PRINT FREQUENCY</h5>
              <p>
                {apiData?.MaxPrintingHour
                  ? convertTo12HourFormat(apiData?.MaxPrintingHour?.Hour)
                  : "NA"}
              </p>
              <footer>{`${fromDate} - ${toDate}`}</footer>
            </div>
            <div className={styles.summaryColumn}>
              <h5>DAILY TOTAL</h5>
              <Bar data={data} options={options} />
              <footer>{`${fromDate} - ${toDate}`}</footer>
            </div>
          </div>

          <div style={{ textAlign: "right", margin: "2% 0 3% 0" }}>
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingRight: "30px" }}
              className={styles.searchBox}
            />
            <span style={{ position: "relative", left: "-30px" }}></span>
          </div>
          <table className="table table-striped table-borderless">
            <thead>
              <tr>
                <th>Job Name</th>
                <th>
                  <label>Job Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="Completed">Completed</option>
                    {/* <option value="Pending">Pending</option> */}
                    <option value="Failed">Failed</option>
                  </select>
                </th>
                <th>User</th>
                <th>
                  Submitted
                  <select
                    value={submittedFilter}
                    onChange={(e) => setSubmittedFilter(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="Today">Today</option>
                    <option value="Yesterday">Yesterday</option>
                    <option value="Last 7 Days">Last 7 Days</option>
                  </select>
                </th>
                <th>Printer ID</th>
              </tr>
            </thead>
            {filteredJobs && filteredJobs?.length > 0 ? (
              <tbody>
                {filteredJobs &&
                  filteredJobs?.map((job: any, index: any) => (
                    <tr key={index}>
                      <td>{job.jobName}</td>
                      <td>{job.jobStatus}</td>
                      <td>{job.user}</td>
                      <td>{job.submitted}</td>
                      <td>{job.printerId}</td>
                    </tr>
                  ))}
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <td colSpan={5} className={styles.recordNotFound}>
                    {apiData == null ? "Loading..." : "Record Not Found"}
                  </td>
                </tr>
              </tbody>
            )}
          </table>
          <div className={styles.pagination}>
            <button onClick={handlePreviousPage} disabled={page === 1}>
              Previous
            </button>
            <span>
              Page {page} of{" "}
              {apiData?.datawithRecordCount.RecordCount
                ? Math.ceil(
                    apiData?.datawithRecordCount.RecordCount / rowsPerPage
                  )
                : 0}
            </span>
            <button
              onClick={handleNextPage}
              disabled={
                !apiData?.datawithRecordCount?.RecordCount ||
                page ===
                  Math.ceil(
                    apiData?.datawithRecordCount?.RecordCount / rowsPerPage
                  )
              }
            >
              Next
            </button>
          </div>
          <div className={styles.rowsPerPage}>
            <label htmlFor="rowsPerPage">Rows per page:</label>
            <select
              id="rowsPerPage"
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={40}>40</option>
              <option value={50}>50</option>
            </select>
          </div>
        </>
      )}
    </div>
  );
};

export default PrintHistory;
